import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { isNavGroup, type NavEntry, type NavGroup, type NavLeaf } from '@/features/auth/types'
import { useSidebarState } from './useSidebarState'
import { MIGRATED_ROUTES } from './migratedRoutes'
import { NavIcon } from './navIcons'
import { navLabel, sectionNav } from './navPresentation'

/** Internal route if this nav slot has been migrated to React, else null. */
function migratedPath(key: string): string | null {
  return MIGRATED_ROUTES[key] ?? null
}

/** Path a leaf points at - its React route, else its legacy href's path. */
function leafPath(leaf: NavLeaf): string | null {
  const to = migratedPath(leaf.key)
  if (to) return to
  try {
    return new URL(leaf.href, window.location.origin).pathname
  } catch {
    return null
  }
}

const itemBase =
  'flex h-11 w-full items-center gap-3.5 rounded-full px-4 text-left text-base font-normal transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
const itemIdle = 'text-ink hover:bg-nav-hover'
const itemActive = 'bg-nav-active [font-weight:650] text-nav-active-text'

const childBase =
  'relative flex h-10 w-full items-center gap-3 rounded-full px-3.5 text-left text-base font-normal transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
const childIdle = 'text-ink hover:bg-nav-hover'
const childActive = 'bg-nav-active font-semibold text-nav-active-text'

interface Flyout {
  key: string
  top: number
  left: number
}

/**
 * Renders exactly what GET /v2/auth/session's `nav` field returns — the same
 * permission-filtered tree internal/core/config/navigation_registry.py
 * computes for the legacy sidebar. No permission logic is duplicated here;
 * an item simply isn't in the list if the user can't see it.
 *
 * `variant="drawer"` is the same tree for small screens (AppLayout's
 * off-canvas menu below the md breakpoint): always the expanded
 * presentation, no collapse toggle, and it leaves the stored rail
 * preference alone. The rail itself is hidden below md.
 */
export default function Sidebar({ variant = 'rail' }: { variant?: 'rail' | 'drawer' }) {
  const { nav } = useAuth()
  const isDrawer = variant === 'drawer'
  const { expanded: railExpanded, toggle } = useSidebarState()
  const expanded = isDrawer || railExpanded
  const location = useLocation()
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  // Collapsed rail only: a group's children open in a floating menu beside
  // the rail rather than being squeezed into it. Positioned `fixed` from the
  // trigger's rect because the <nav> scrolls (overflow-y-auto), which would
  // clip an absolutely positioned child.
  const [flyout, setFlyout] = useState<Flyout | null>(null)
  const flyoutRef = useRef<HTMLDivElement>(null)

  // The single nav leaf matching the current URL - longest path wins, so a
  // detail route (/companies/42) still highlights its list page.
  const activeKey = useMemo(() => {
    let best: { key: string; len: number } | null = null
    const visit = (leaf: NavLeaf) => {
      const path = leafPath(leaf)
      if (!path) return
      const hit = location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`))
      if (hit && (!best || path.length > best.len)) best = { key: leaf.key, len: path.length }
    }
    nav.forEach((entry) => (isNavGroup(entry) ? entry.children.forEach(visit) : visit(entry)))
    return (best as { key: string } | null)?.key ?? null
  }, [nav, location.pathname])

  useEffect(() => setFlyout(null), [location.pathname, location.search, expanded])

  useEffect(() => {
    if (!flyout) return
    function onPointer(e: globalThis.MouseEvent) {
      const target = e.target as Node
      if (flyoutRef.current?.contains(target)) return
      if ((target as Element).closest?.(`[data-flyout-trigger="${flyout!.key}"]`)) return
      setFlyout(null)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFlyout(null)
    }
    function onResize() {
      setFlyout(null)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [flyout])

  // A client role with no permission-gated nav entries at all (the common
  // case - clients only ever see their own portfolio/device pages, reached
  // via direct links rather than a sidebar) would otherwise still render
  // an empty nav rail plus its expand/collapse toggle, wasting that width
  // for no navigable content. Rendering nothing here instead lets
  // AppLayout's flex row give <main> the full width automatically - no
  // change needed there.
  if (nav.length === 0) return null

  function onGroupClick(group: NavGroup, hasActive: boolean, e: MouseEvent<HTMLButtonElement>) {
    if (expanded) {
      setOpenGroups((prev) => ({ ...prev, [group.key]: !(prev[group.key] ?? hasActive) }))
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    setFlyout((prev) => (prev?.key === group.key ? null : { key: group.key, top: rect.top - 8, left: rect.right + 12 }))
  }

  function renderLeafLink(leaf: NavLeaf, className: string, children: ReactNode, title?: string) {
    const to = migratedPath(leaf.key)
    const current = leaf.key === activeKey ? 'page' : undefined
    return to ? (
      <Link to={to} title={title} aria-current={current} className={className}>
        {children}
      </Link>
    ) : (
      <a href={leaf.href} title={title} aria-current={current} className={className}>
        {children}
      </a>
    )
  }

  function renderChildren(group: NavGroup, inFlyout: boolean) {
    return group.children.map((child) => {
      const active = child.key === activeKey
      const className = `${childBase} ${active ? childActive : childIdle}`
      return (
        <li key={child.key}>
          {renderLeafLink(
            child,
            className,
            <>
              {/* Guide-line marker for the active child (expanded tree only). */}
              {active && !inFlyout && (
                <span aria-hidden="true" className="absolute -left-3 bottom-2.5 top-2.5 w-[3px] rounded-full bg-brand" />
              )}
              {inFlyout && <NavIcon navKey={child.key} fallbackSvg={child.icon_svg} className="h-[18px] w-[18px]" />}
              <span className="truncate">{navLabel(child.key, child.label)}</span>
            </>,
            navLabel(child.key, child.label),
          )}
        </li>
      )
    })
  }

  function renderEntry(entry: NavEntry) {
    if (isNavGroup(entry)) {
      const hasActive = entry.children.some((c) => c.key === activeKey)
      const isOpen = expanded ? (openGroups[entry.key] ?? hasActive) : flyout?.key === entry.key
      const tone = hasActive
        ? expanded
          ? 'font-semibold text-nav-active-text hover:bg-nav-hover'
          : 'bg-nav-active text-nav-active-text'
        : itemIdle
      return (
        <li key={entry.key}>
          <button
            type="button"
            data-flyout-trigger={entry.key}
            onClick={(e) => onGroupClick(entry, hasActive, e)}
            title={navLabel(entry.key, entry.label)}
            aria-expanded={isOpen}
            className={`${itemBase} ${tone} ${expanded ? '' : 'justify-center px-0'}`}
          >
            <NavIcon navKey={entry.key} fallbackSvg={entry.icon_svg} />
            {expanded && (
              <>
                <span className="flex-1 truncate">{navLabel(entry.key, entry.label)}</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className={`h-[18px] w-[18px] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </>
            )}
          </button>
          {expanded && (
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="min-h-0 overflow-hidden">
                <ul
                  // `invisible` (not just height 0) keeps a closed group's
                  // links out of the tab order; transitioning visibility
                  // defers it until the collapse animation has finished.
                  className={`mb-1 ml-[26px] mt-0.5 flex flex-col gap-0.5 border-l-[1.5px] border-nav-line pl-2.5 transition-[visibility] duration-200 ${isOpen ? 'visible' : 'invisible'}`}
                >
                  {renderChildren(entry, false)}
                </ul>
              </div>
            </div>
          )}
          {!expanded && isOpen && flyout && (
            <div
              ref={flyoutRef}
              style={{ top: flyout.top, left: flyout.left }}
              className="fixed z-50 w-[280px] rounded-2xl bg-card p-2 shadow-[0_10px_28px_rgba(16,40,26,0.16),0_1px_3px_rgba(16,40,26,0.08)]"
            >
              <div className="px-3.5 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-nav-muted">
                {navLabel(entry.key, entry.label)}
              </div>
              <ul className="flex flex-col gap-0.5">{renderChildren(entry, true)}</ul>
            </div>
          )}
        </li>
      )
    }

    const active = entry.key === activeKey
    return (
      <li key={entry.key}>
        {renderLeafLink(
          entry,
          `${itemBase} ${active ? itemActive : itemIdle} ${expanded ? '' : 'justify-center px-0'}`,
          <>
            <NavIcon navKey={entry.key} fallbackSvg={entry.icon_svg} />
            {expanded && <span className="truncate">{navLabel(entry.key, entry.label)}</span>}
          </>,
          navLabel(entry.key, entry.label),
        )}
      </li>
    )
  }

  return (
    <nav
      aria-label="Main"
      className={
        isDrawer
          ? 'min-h-0 flex-1 overflow-y-auto bg-shell px-3 pb-8 pt-1'
          : `hidden h-full shrink-0 overflow-y-auto bg-shell pb-8 pt-1 transition-[width] duration-200 md:block ${
              expanded ? 'w-[264px] px-3' : 'w-[80px] px-3'
            }`
      }
    >
      {!isDrawer && (
      <div className={`flex pb-1 ${expanded ? 'justify-end px-1' : 'justify-center'}`}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-nav-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="h-5 w-5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
            <path d={expanded ? 'm16 15-3-3 3-3' : 'm14 9 3 3-3 3'} />
          </svg>
        </button>
      </div>
      )}
      <ul className={`flex flex-col gap-0.5 ${expanded ? '' : 'items-center [&>li]:w-14'}`}>
        {sectionNav(nav).map((section, i) => [
          section.title !== null &&
            (expanded ? (
              <li
                key={`section-${i}`}
                className="px-4 pb-1.5 pt-[18px] text-[11px] font-semibold uppercase leading-[normal] tracking-[0.08em] text-nav-muted"
              >
                {section.title}
              </li>
            ) : (
              // Collapsed rail: a short divider stands in for the heading.
              <li key={`section-${i}`} aria-hidden="true" className="flex justify-center py-2.5">
                <span className="h-px w-6 bg-nav-line" />
              </li>
            )),
          ...section.entries.map(renderEntry),
        ])}
      </ul>
    </nav>
  )
}
